// src/components/OrderList.js

import React, { useState, useEffect } from 'react';
import './OrderList.css';

// Import your Firebase configuration and Firestore functions
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase'; // Ensure this path is correct

const OrderList = () => {
    const [orders, setOrders] = useState([]);
    const [filteredOrders, setFilteredOrders] = useState([]);
    const [filterStatus, setFilterStatus] = useState('All');
    const [deliveryPersons, setDeliveryPersons] = useState([]);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [assignedPersonId, setAssignedPersonId] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // --- DATA FETCHING & FILTERING ---

    useEffect(() => {
        const fetchData = async () => {
            try {
                const ordersSnapshot = await getDocs(collection(db, "grocery orders"));
                const ordersList = ordersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

                const deliveryPersonsSnapshot = await getDocs(collection(db, "delivery_persons"));
                const deliveryPersonsList = deliveryPersonsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

                setOrders(ordersList);
                setFilteredOrders(ordersList);
                setDeliveryPersons(deliveryPersonsList);
            } catch (err) {
                console.error("Error fetching data: ", err);
                setError("Failed to load data from Firestore. Please check your setup.");
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, []);

    useEffect(() => {
        if (filterStatus === 'All') {
            setFilteredOrders(orders);
        } else {
            const filtered = orders.filter(order => order.status === filterStatus);
            setFilteredOrders(filtered);
        }
    }, [filterStatus, orders]);

    // --- EVENT HANDLERS ---

    const handleViewDetails = (order) => {
        setSelectedOrder(order);
        setAssignedPersonId(order.deliveryPerson?.id || '');
        setIsModalOpen(true);
    };

    const closeModal = () => setIsModalOpen(false);

    const handleUpdateStatus = async (newStatus) => {
        if (!selectedOrder) return;
        try {
            const orderRef = doc(db, "grocery orders", selectedOrder.id);
            await updateDoc(orderRef, { status: newStatus });
            const updatedOrders = orders.map(o => o.id === selectedOrder.id ? { ...o, status: newStatus } : o);
            setOrders(updatedOrders);
            setSelectedOrder({ ...selectedOrder, status: newStatus });
        } catch (err) { alert("Failed to update status."); }
    };
    
    const handleAssignDelivery = async () => {
        if (!assignedPersonId) return alert("Please select a delivery person.");
        const person = deliveryPersons.find(p => p.id === assignedPersonId);
        try {
            const orderRef = doc(db, "grocery orders", selectedOrder.id);
            await updateDoc(orderRef, { deliveryPerson: person, status: 'OUT FOR DELIVERY' });
            const updatedOrders = orders.map(o => o.id === selectedOrder.id ? { ...o, deliveryPerson: person, status: 'OUT FOR DELIVERY' } : o);
            setOrders(updatedOrders);
            closeModal();
        } catch (err) { alert("Failed to assign delivery person."); }
    };

    // --- RENDER LOGIC ---

    if (isLoading) return <div className="loading-state">Loading orders...</div>;
    if (error) return <div className="error-state">{error}</div>;

    return (
        <div className="dashboard-container">
            <div className="dashboard-header">
                <h1>Orders Dashboard</h1>
                <div className="filter-container">
                    <label htmlFor="status-filter">Filter by Status:</label>
                    <select id="status-filter" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                        <option value="All">All</option>
                        <option value="PAYMENT SUCCESSFUL & ORDER PLACED">Placed</option>
                        <option value="Accepted">Accepted</option>
                        <option value="OUT FOR DELIVERY">Out for Delivery</option>
                        <option value="Delivered">Delivered</option>
                    </select>
                </div>
            </div>

            <div className="table-container">
                <table>
                    <thead>
                        <tr>
                            <th>Order ID</th>
                            <th>Customer Name</th>
                            <th>Total Bill</th>
                            <th>Status</th>
                            {/* <th>Action</th> */}
                        </tr>
                    </thead>
                    <tbody>
                        {filteredOrders.length > 0 ? (
                            filteredOrders.map(order => (
                                <tr key={order.id}>
                                    <td data-label="Order ID" className="order-id-cell">{order.orderId || order.id}</td>
                                    <td data-label="Customer">{order.customerName || 'N/A'}</td>
                                    <td data-label="Total Bill">₹{order.billing?.totalBill || '0.00'}</td>
                                    <td data-label="Status">
                                        <span className={`status-badge status-${order.status?.toLowerCase().replace(/[\s&]+/g, '-') || 'default'}`}>
                                            {order.status}
                                        </span>
                                    </td>
                                    {/* <td data-label="Action">
                                        <button className="manage-btn-table" onClick={() => handleViewDetails(order)}>Manage</button>
                                    </td> */}
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="5" className="no-orders-cell">No orders found with this status.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Complete Modal JSX */}
            {isModalOpen && selectedOrder && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h2>Manage Order: {selectedOrder.orderId || selectedOrder.id}</h2>
                        
                        <div className="modal-section">
                            <h4>Customer & Delivery Details</h4>
                            <p><strong>Customer Name:</strong> {selectedOrder.customerName || 'N/A'}</p>
                            <p><strong>Receiver Name:</strong> {selectedOrder.deliveryAddress?.receiverName || 'N/A'}</p>
                            <p><strong>Phone Number:</strong> {selectedOrder.deliveryAddress?.receiverPhone || 'N/A'}</p>
                            <p><strong>Full Address:</strong>
                                <a href={`https://www.google.com/maps/search/?api=1&query=${selectedOrder.deliveryAddress?.lat},${selectedOrder.deliveryAddress?.lng}`}
                                   target="_blank" rel="noopener noreferrer" className="address-link" title="Click to view on Google Maps">
                                   📍 {selectedOrder.deliveryAddress?.addressString || 'Address not available'}
                                </a>
                            </p>
                        </div>

                        <div className="modal-section">
                            <h4>Ordered Items</h4>
                            {selectedOrder.items && selectedOrder.items.length > 0 ? (
                                <ul className="item-list">
                                    <li className="item-list-header"><span>Product</span><span>Qty</span><span>Price</span></li>
                                    {selectedOrder.items.map((item, index) => (
                                        <li key={item.id || index} className="item-list-item">
                                            <span>{item.name}</span><span>{item.quantityInCart}</span><span>₹{item.price?.toFixed(2)}</span>
                                        </li>
                                    ))}
                                </ul>
                            ) : (<p className="no-items">No items listed for this order.</p>)}
                        </div>

                        <div className="modal-section">
                           <h4>Payment Verification</h4>
                           {selectedOrder.paymentScreenshotBase64 ? (
                               <>
                                   <div className="payment-screenshot"><img src={selectedOrder.paymentScreenshotBase64} alt="Payment Screenshot" /></div>
                                   <div className="button-group">
                                       <a href={selectedOrder.paymentScreenshotBase64} download={`payment_${selectedOrder.orderId}.png`} className="action-btn download-btn">Download</a>
                                       <button className="action-btn verify-btn" onClick={() => handleUpdateStatus('Accepted')} disabled={selectedOrder.status !== 'PAYMENT SUCCESSFUL & ORDER PLACED'}>Verify & Accept</button>
                                   </div>
                               </>
                           ) : <p className="no-screenshot">No screenshot provided (Method: {selectedOrder.paymentMethod || 'N/A'}).</p>}
                        </div>
                        
                        <div className="modal-section">
                            <h4>Assign & Ship</h4>
                            {/* --- THIS IS THE CORRECTED LINE --- */}
                            <select value={assignedPersonId} onChange={(e) => setAssignedPersonId(e.target.value)} className="delivery-select">
                                <option value="">-- Select Delivery Person --</option>
                                {deliveryPersons.map(p => <option key={p.id} value={p.id}>{p.name} - {p.vehicleNumber}</option>)}
                            </select>
                            <button className="action-btn assign-btn" onClick={handleAssignDelivery}>Assign to this Order</button>
                        </div>

                        <button className="close-btn" onClick={closeModal}>Close</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OrderList;